import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserCheck, Car, Search } from "lucide-react";

const FALLBACK_SERVICES = ["Battery", "Motor Service", "Charger Services", "Diagnostic Check", "General Maintenance"];
const STATUS_OPTIONS = ["Pending", "In Progress", "Completed", "Delivered"];

const JobCardModal = ({ open, onClose, editingCard }) => {
  const [formData, setFormData] = useState({
    type_of_service: "", customer_name: "", customer_email: "",
    customer_contact: "", vehicle_details: "", vehicle_id: null,
    status: "Pending",
    submitted_date: new Date().toISOString().split('T')[0],
    delivery_date: "", picked_up_by: "", notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [linkedParty, setLinkedParty] = useState(null);

  // Vehicle picker state
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehicleResults, setVehicleResults] = useState([]);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Services from DB
  const [dbServices, setDbServices] = useState([]);
  const [serviceNames, setServiceNames] = useState(FALLBACK_SERVICES);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/services/active`).then(r => {
        const names = r.data.map(s => s.name);
        setDbServices(r.data);
        setServiceNames(names.length > 0 ? names : FALLBACK_SERVICES);
      }).catch(() => setServiceNames(FALLBACK_SERVICES));
    }
  }, [open]);

  useEffect(() => {
    if (editingCard) {
      setFormData({
        type_of_service: editingCard.type_of_service || "",
        customer_name: editingCard.customer_name || "",
        customer_email: editingCard.customer_email || "",
        customer_contact: editingCard.customer_contact || "",
        vehicle_details: editingCard.vehicle_details || "",
        vehicle_id: editingCard.vehicle_id || null,
        status: editingCard.status || "Pending",
        submitted_date: editingCard.submitted_date || new Date().toISOString().split('T')[0],
        delivery_date: editingCard.delivery_date || "",
        picked_up_by: editingCard.picked_up_by || "",
        notes: editingCard.notes || ""
      });
      setSelectedVehicle(null);
      setLinkedParty(null);
      // If editing card has vehicle_id, try to fetch the vehicle
      if (editingCard.vehicle_id) {
        axios.get(`${API}/vehicles/${editingCard.vehicle_id}`).then(r => {
          setSelectedVehicle(r.data);
          setVehicleSearch(r.data.registration_number);
        }).catch(() => {});
      } else {
        setVehicleSearch(editingCard.vehicle_details || "");
      }
    } else {
      setFormData({
        type_of_service: "", customer_name: "", customer_email: "",
        customer_contact: "", vehicle_details: "", vehicle_id: null,
        status: "Pending",
        submitted_date: new Date().toISOString().split('T')[0],
        delivery_date: "", picked_up_by: "", notes: ""
      });
      setLinkedParty(null);
      setSelectedVehicle(null);
      setVehicleSearch("");
    }
    setSuggestions([]);
    setShowSuggestions(false);
    setVehicleResults([]);
    setShowVehicleDropdown(false);
  }, [editingCard, open]);

  const searchParties = useCallback(async (query) => {
    if (!query || query.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await axios.get(`${API}/parties/search-mobile?q=${encodeURIComponent(query)}`);
      setSuggestions(res.data);
      setShowSuggestions(res.data.length > 0);
    } catch { setSuggestions([]); }
  }, []);

  const handleContactChange = (value) => {
    setFormData(prev => ({ ...prev, customer_contact: value }));
    setLinkedParty(null);
    searchParties(value);
  };

  const selectSuggestion = (party) => {
    setFormData(prev => ({
      ...prev,
      customer_name: party.party_name,
      customer_email: party.email || prev.customer_email,
      customer_contact: party.mobile_number
    }));
    setLinkedParty(party);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Vehicle search
  const searchVehicles = useCallback(async (query) => {
    if (!query || query.length < 2) { setVehicleResults([]); setShowVehicleDropdown(false); return; }
    try {
      const r = await axios.get(`${API}/vehicles/search?q=${encodeURIComponent(query)}`);
      setVehicleResults(r.data);
      setShowVehicleDropdown(r.data.length > 0);
    } catch { setVehicleResults([]); }
  }, []);

  const handleVehicleSearchChange = (value) => {
    setVehicleSearch(value);
    setSelectedVehicle(null);
    setFormData(prev => ({ ...prev, vehicle_details: value, vehicle_id: null }));
    searchVehicles(value);
  };

  const selectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    const details = `${vehicle.registration_number} - ${vehicle.make} ${vehicle.model}`;
    setVehicleSearch(vehicle.registration_number);
    setFormData(prev => ({
      ...prev,
      vehicle_details: details,
      vehicle_id: vehicle.id,
      customer_name: prev.customer_name || vehicle.customer_name || "",
      customer_contact: prev.customer_contact || vehicle.customer_contact || "",
    }));
    setShowVehicleDropdown(false);
    setVehicleResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_contact.trim()) { toast.error("Mobile number is mandatory"); return; }
    setLoading(true);
    try {
      if (editingCard) {
        await axios.put(`${API}/jobcards/${editingCard.id}`, formData);
        toast.success("Job card updated successfully");
      } else {
        await axios.post(`${API}/jobcards`, formData);
        toast.success("Job card created successfully");
      }
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  // Get price hint from DB service
  const selectedService = dbServices.find(s => s.name === formData.type_of_service);

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk' }}>
            {editingCard ? "Edit Job Card" : "Create New Job Card"}
          </DialogTitle>
          <DialogDescription>
            {editingCard ? "Update the job card details below" : "Fill in the details to create a new job card"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type of Service *</Label>
              <Select value={formData.type_of_service} onValueChange={(v) => setFormData({ ...formData, type_of_service: v })} required>
                <SelectTrigger data-testid="service-type-select" className="h-11"><SelectValue placeholder="Select service type" /></SelectTrigger>
                <SelectContent>
                  {serviceNames.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedService && selectedService.base_price > 0 && (
                <p className="text-[11px] text-[#64748B]">Base price: ₹{selectedService.base_price.toLocaleString()} {selectedService.estimated_duration ? `· ${selectedService.estimated_duration}` : ""}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })} required>
                <SelectTrigger data-testid="status-select" className="h-11"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Mobile number with auto-suggest */}
          <div className="space-y-2 relative">
            <Label>Customer Contact (Mobile) *</Label>
            <Input data-testid="customer-contact-input" type="text" placeholder="Enter mobile number (mandatory)"
              value={formData.customer_contact} onChange={(e) => handleContactChange(e.target.value)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              required className="h-11" />
            {linkedParty && (
              <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
                <UserCheck className="w-3.5 h-3.5" />
                Linked to existing party: {linkedParty.party_name}
              </div>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 top-[72px] left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="customer-suggestions">
                <div className="p-2 text-xs text-slate-500 border-b">Existing customers:</div>
                {suggestions.map(p => (
                  <button key={p.id} type="button" data-testid={`suggestion-${p.id}`} onClick={() => selectSuggestion(p)}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 transition-colors flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-800">{p.party_name}</span>
                    <span className="text-xs text-slate-500">{p.mobile_number}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input data-testid="customer-name-input" type="text" placeholder="Enter customer name"
                value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} required className="h-11" />
            </div>
            <div className="space-y-2">
              <Label>Customer Email *</Label>
              <Input data-testid="customer-email-input" type="email" placeholder="customer@email.com"
                value={formData.customer_email} onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })} required className="h-11" />
            </div>
          </div>

          {/* Vehicle picker */}
          <div className="space-y-2 relative">
            <Label className="flex items-center gap-1.5">
              <Car className="w-3.5 h-3.5 text-[#64748B]" /> Vehicle *
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input data-testid="vehicle-search-input" type="text" placeholder="Search by registration number, make, or model..."
                value={vehicleSearch} onChange={(e) => handleVehicleSearchChange(e.target.value)}
                onFocus={() => { if (vehicleResults.length > 0) setShowVehicleDropdown(true); }}
                onBlur={() => setTimeout(() => setShowVehicleDropdown(false), 200)}
                required className="h-11 pl-9" />
            </div>
            {selectedVehicle && (
              <div className="flex items-center gap-2 text-xs text-[#3B82F6] mt-1 bg-[#EFF6FF] px-2.5 py-1.5 rounded-lg">
                <Car className="w-3.5 h-3.5" />
                <span className="font-semibold">{selectedVehicle.registration_number}</span>
                <span className="text-[#64748B]">{selectedVehicle.make} {selectedVehicle.model}</span>
                {selectedVehicle.color && <span className="text-[#94A3B8]">· {selectedVehicle.color}</span>}
              </div>
            )}
            {showVehicleDropdown && vehicleResults.length > 0 && (
              <div className="absolute z-50 top-[72px] left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="vehicle-suggestions">
                <div className="p-2 text-xs text-slate-500 border-b flex items-center gap-1.5">
                  <Car className="w-3 h-3" /> Registered vehicles:
                </div>
                {vehicleResults.map(v => (
                  <button key={v.id} type="button" data-testid={`vehicle-option-${v.id}`} onClick={() => selectVehicle(v)}
                    className="w-full text-left px-3 py-2.5 hover:bg-[#EFF6FF] transition-colors border-b border-[#F1F5F9] last:border-0">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-bold text-[#1E293B]">{v.registration_number}</span>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F8FAFC] text-[#64748B]">{v.vehicle_type}</span>
                    </div>
                    <p className="text-[11px] text-[#64748B] mt-0.5">{v.make} {v.model} {v.color ? `· ${v.color}` : ""}</p>
                    {v.customer_name && <p className="text-[10px] text-[#94A3B8]">{v.customer_name}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Submitted Date *</Label>
              <Input data-testid="submitted-date-input" type="date" value={formData.submitted_date}
                onChange={(e) => setFormData({ ...formData, submitted_date: e.target.value })} required className="h-11" />
            </div>
            <div className="space-y-2">
              <Label>Delivery Date (Optional)</Label>
              <Input data-testid="delivery-date-input" type="date" value={formData.delivery_date}
                onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })} className="h-11" />
              <p className="text-xs text-slate-500">Auto-filled when status is "Delivered"</p>
            </div>
            <div className="space-y-2">
              <Label>Picked Up By</Label>
              <Input data-testid="picked-up-by-input" type="text" placeholder="Name of person (optional)"
                value={formData.picked_up_by} onChange={(e) => setFormData({ ...formData, picked_up_by: e.target.value })} className="h-11" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea data-testid="notes-textarea" placeholder="Additional notes or comments (optional)"
              value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button data-testid="cancel-button" type="button" variant="outline" onClick={() => onClose(false)} disabled={loading} className="border-slate-300">Cancel</Button>
            <Button data-testid="submit-jobcard-button" type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white">
              {loading ? "Saving..." : editingCard ? "Update Job Card" : "Create Job Card"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobCardModal;
